import { QueryResult, useMutation } from "@apollo/client";
import { IonContent, IonItem, IonPopover, IonSkeletonText } from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Button from "@components/buttons/Button";
import styles from "@components/loaders/LoaderStyles.module.css";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import useWindowSize from "@hooks/useWindowSize";
import { OnboardingStepEnum } from "@typing/Enums";
import {
  Exact,
  GetClientWithJourneysQuery,
  JourneyCreateDocument,
  JourneyCreateMutation,
  JourneyCreateMutationVariables
} from "@typing/Generated";
import { getCareProfileName } from "@utils/modelUtils";
import { isIosNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";
import { truncate } from "@utils/stringUtils";

type Props = {
  clientQuery: QueryResult<
    GetClientWithJourneysQuery,
    Exact<{
      id: string;
    }>
  >;
  journeyId: string;
};

const JourneySelector = ({ clientQuery, journeyId }: Props) => {
  const popoverOpener = useOpener();
  const intl = useIntl();
  const client = clientQuery.data?.client;
  const myClient = useMyClient();
  const history = useHistory();

  const [journeyCreate] = useMutation<JourneyCreateMutation, JourneyCreateMutationVariables>(JourneyCreateDocument);

  const handleJourneyCreate = () => {
    if (client) {
      journeyCreate({
        variables: {
          clientId: client.id
        }
      }).then(response => {
        const journey = response.data?.journeyCreate?.journey;
        if (journey && myClient) {
          history.push(
            `/onboarding/journeys/${journey.id}/profile-questions/${OnboardingStepEnum.USE_CASE_PREFERENCES}`
          );
        }
      });
    }
  };

  const handleJourneyIdSelect = (value: string) => {
    history.push(journeyRoute({ journeyId: value, linkAfterJourney: "/home", myClient }));
  };

  const windowSize = useWindowSize();

  const getTruncationLength = (width: number) => {
    const breakpoints = [
      { length: 7, width: 320 },
      { length: 21, width: 440 },
      { length: 36, width: 576 }
    ];

    const lowerBound = breakpoints.reduce((prev, curr) => (curr.width <= width ? curr : prev));
    const upperBound = breakpoints.find(b => b.width > width) || lowerBound;

    const range = upperBound.width - lowerBound.width;
    const factor = range === 0 ? 0 : (width - lowerBound.width) / range;

    return Math.round(lowerBound.length + factor * (upperBound.length - lowerBound.length));
  };

  if (!client) return <IonSkeletonText animated className={styles.skeletonCareProfileName} />;

  const currentRole = client.roles.find(role => role.journey.id === journeyId);

  const clientHasUnattachedCarePlanningPackage = client.eligiblePackageGrants.some(
    grant => grant.hasCarePlanning && !grant.carePlanningJourneyId
  );

  return (
    <>
      <ButtonLink color="white" id="popover-button" onClick={popoverOpener.open} stylized={false}>
        <div className="flex items-center flex-wrap">
          <div className="no-wrap">
            <Heading2 bold color="black">
              <FormattedMessage id="pages.carePlan.client.caringFor" />
            </Heading2>
          </div>
          <Nbsp />
          <div className="flex items-center">
            <Heading2 bold color="primary-300">
              {currentRole &&
                truncate(
                  getCareProfileName({ intl, journey: currentRole.journey }),
                  getTruncationLength(windowSize.raw.width)
                )}
            </Heading2>

            <Nbsp />
            <Icon color="gray-300" icon="chevronDown" spacingStart />
          </div>
        </div>
      </ButtonLink>

      <IonPopover
        alignment="center"
        isOpen={popoverOpener.isOpen}
        onIonPopoverDidDismiss={popoverOpener.close}
        onIonPopoverDidPresent={popoverOpener.open}
        onIonPopoverWillPresent={popoverOpener.open}
        side="bottom"
        trigger="popover-button"
      >
        <IonContent>
          {client.roles.map(role => (
            <IonItem
              button
              detail={false}
              key={role.id}
              lines="none"
              onClick={() => handleJourneyIdSelect(role.journey.id)}
            >
              <BodyCopy bold color={role.journey.id === journeyId ? "primary-300" : "dark"}>
                {getCareProfileName({ intl, journey: role.journey })}
              </BodyCopy>
              {role.journey.id === journeyId && <IconBase color="primary-300" icon="checkmark" slot="end" />}
            </IonItem>
          ))}
          {myClient &&
            (client.hasUnlimitedMinutes || client.timeBalance > 0 || clientHasUnattachedCarePlanningPackage ? (
              <IonItem button detail={false} lines="none" onClick={handleJourneyCreate}>
                <BodyCopy bold>
                  <FormattedMessage id="pages.client.account.addAJourney" />
                </BodyCopy>
                <Icon icon="add" slot="end" />
              </IonItem>
            ) : (
              <div className="padding-20-bottom padding-20-start padding-20-end">
                <hr className="margin-20-bottom" color="gray-100" />
                <div>
                  <BodyCopy bold color="dark">
                    <FormattedMessage id="components.JourneySelector.addHours.title" />
                  </BodyCopy>
                </div>
                <div className="margin-20-top">
                  <BodyCopy color="gray" size="small">
                    <FormattedMessage id="components.JourneySelector.addHours.subtitle" />
                  </BodyCopy>
                </div>
                {!isIosNativeApp() && (
                  <Button
                    className="margin-20-top"
                    routerLink={journeyRoute({
                      journeyId,
                      linkAfterJourney: "/account?purchaseHours=true",
                      myClient,
                      preventScroll: false
                    })}
                  >
                    <FormattedMessage id="pages.client.account.goToMyAccount" />
                  </Button>
                )}
              </div>
            ))}
        </IonContent>
      </IonPopover>
    </>
  );
};

export default JourneySelector;
